import Vue from 'vue';
import VueRouter, {NavigationGuardNext, Route, RouteConfig} from 'vue-router';
import Axios from 'axios';

import Invoices from '@/pages/Invoices.vue';

import Coupons from '@/pages/Coupons/Coupons.vue';
import PersonalAccountLayout from '@/layouts/PersonalAccountLayout.vue';
import Profile from '@/pages/Profile/Profile.vue';
import Address from '@/pages/Profile/Address.vue';
import ManualAddress from '@/pages/Profile/ManualAddress.vue';


import * as mutationTypes from '@/store/mutation-types'
import ChangeID from "@/pages/Profile/ChangeID.vue";
import ChangeProofOfAddress from "@/pages/Profile/ChangeProofOfAddress.vue";
import ChangeInsuranceStatement from "@/pages/Profile/ChangeInsuranceStatement.vue";
import ChangeLicense from "@/pages/Profile/ChangeLicense.vue";
import Newsletter from "@/pages/Settings/Newsletter.vue";
import NotFound from "@/pages/NotFound.vue";
import {Store} from "vuex";

Vue.use(VueRouter);

import {slugs} from '@/constants/slugs';
import {f2mSlugs} from '@/constants/f2mSlugs';
import AddCoupon from "@/pages/Coupons/AddCoupon.vue";
import Settings from "@/pages/Settings/Settings.vue";
import Bookings from "@/pages/Bookings/Bookings.vue";
import BookingsCarsharingDetail from "@/pages/Bookings/BookingsCarsharingDetail.vue";
import BookingsRentDetail from "@/pages/Bookings/BookingsRentDetail.vue";
import BookingsParkDetail from "@/pages/Bookings/BookingsParkDetail.vue";
import DownloadData from "@/pages/Settings/DownloadData.vue";

export const getSlug = (language: string, country: string, name: string | null | undefined, f2m: boolean = false) => {
    if (!name) {
        return;
    }
    const searchSlugs: any = f2m ? f2mSlugs : slugs;
    return searchSlugs[name][`${language.toLowerCase()}_${country.toUpperCase()}`]
        || searchSlugs[name][language.toLowerCase()]
        || searchSlugs[name]['*'];
}

const buildRouteSlugs = ({prefixUrl, language, country, routes, store}: any) => {
    routes = routes.map((route: any) => {
        if (route.name && slugs[route.name]) {
            const slug = getSlug(language, country, route.name);
            route.path = slug ? `${prefixUrl}/${slug}` : prefixUrl;

            if (route.protected) {
                route.beforeEnter = (to: Route, from: Route, next: NavigationGuardNext) => protectedRoute(to, from, next, store);
            }

            if (route.children) {
                route.children = buildRouteSlugs({prefixUrl: route.path, language, country, routes: route.children, store});
            }
            return route;
        }
        return route;
    });
    return routes;
};

const protectedRoute = (to: Route, from: Route, next: NavigationGuardNext, store: Store<any>):void => {
    //get auth token only from local storage
    if(store.state.isAuthenticated) {
        next();
    } else {
        store.commit(mutationTypes.UPDATE_SHOW_GLOBAL_LOADER, true);
        Axios.get('/user/api/me/', {withCredentials: true})
            .then((data)=>{
                store.commit(mutationTypes.UPDATE_IS_AUTHENTICATED_USER, true);
                next();
                store.commit(mutationTypes.UPDATE_SHOW_GLOBAL_LOADER, false);
            })
            .catch((err) => {
                if (err.response.status === 401) {
                    const baseUrl = `/${Vue.prototype.$language}-${Vue.prototype.$country}`;
                    window.location.href = `${baseUrl}/${getSlug(Vue.prototype.$language, Vue.prototype.$country, 'login', true)}?redirectTo=${window.location.pathname}`
                }
            })
    }
}


const getRoutes = ({store,...params}:any):Array<RouteConfig> => {
    const routes = [
        {
            name: 'personalAccount',
            component: PersonalAccountLayout,
            redirect: { name: 'personalAccountProfile' },
            children: [
                {
                    name: 'personalAccountProfile',
                    component: Profile,
                    protected: true
                },
                {
                    name: 'personalAccountAddress',
                    component: Address,
                    meta: {
                        activeRoute: 'personalAccountProfile',
                    },
                    protected: true
                },
                {
                    name: 'personalAccountManualAddress',
                    component: ManualAddress,
                    meta: {
                        activeRoute: 'personalAccountProfile',
                    },
                    protected: true
                },
                {
                    name: 'personalAccountChangeID',
                    component: ChangeID,
                    meta: {
                        activeRoute: 'personalAccountProfile',
                    },
                    protected: true
                },
                {
                    name: 'personalAccountChangeProofOfAddress',
                    component: ChangeProofOfAddress,
                    meta: {
                        activeRoute: 'personalAccountProfile',
                    },
                    protected: true
                },
                {
                    name: 'personalAccountChangeInsuranceStatement',
                    component: ChangeInsuranceStatement,
                    meta: {
                        activeRoute: 'personalAccountProfile',
                    },
                    protected: true
                },
                {
                    name: 'personalAccountChangeLicense',
                    component: ChangeLicense,
                    meta: {
                        activeRoute: 'personalAccountProfile',
                    },
                    protected: true
                },
                {
                    name: 'personalAccountInvoices',
                    component: Invoices,
                    protected: true
                },
                {
                    name: 'personalAccountCoupons',
                    component: Coupons,
                    protected: true
                },
                {
                    name: 'personalAccountAddCoupon',
                    component: AddCoupon,
                    protected: true
                },
                {
                    name: 'personalAccountSettings',
                    component: Settings,
                    protected: true
                },
                {
                    name: 'personalAccountDownloadData',
                    component: DownloadData,
                    protected: true,
                    meta: {
                        activeRoute: 'personalAccountSettings',
                    },
                },
                {
                    name: 'personalAccountNewsletter',
                    component: Newsletter,
                    protected: true,
                    meta: {
                        activeRoute: 'personalAccountSettings',
                    },
                },
                {
                    name: 'personalAccountBookings',
                    component: Bookings,
                    protected: true,
                },
                {
                    name: 'personalAccountBookingsCarsharingDetail',
                    component: BookingsCarsharingDetail,
                    protected: true,
                    meta: {
                        activeRoute: 'personalAccountBookings',
                    },
                },
                {
                    name: 'personalAccountBookingsRentDetail',
                    component: BookingsRentDetail,
                    protected: true,
                    meta: {
                        activeRoute: 'personalAccountBookings',
                    },
                },
                {
                    name: 'personalAccountBookingsParkDetail',
                    component: BookingsParkDetail,
                    protected: true,
                    meta: {
                        activeRoute: 'personalAccountBookings',
                    },
                }
            ],
        },
        {
            path: '/',
            beforeEnter() {
                window.location.href = `${Vue.prototype.$appBaseUrl}/${Vue.prototype.$language}-${Vue.prototype.$country}/`
            }
        },
        {
            name: 'personalAccountNotFound',
            component: NotFound
        },
        {
            path: '/:catchAll(.*)',
            redirect: { name: 'personalAccountNotFound' },
        },
    ];
    return buildRouteSlugs({...params, routes, store});
};

const getRouter = (params: any):VueRouter => {
    const router = new VueRouter({
        base: '/user/personal-account',
        mode: 'history',
        routes: getRoutes(params),
    });

    router.beforeEach((to: Route, from: Route, next: NavigationGuardNext) => {
        window.scrollTo(0, 0);
        document.body.classList.remove('body--withModalOpened');
        next();
    });

    return router;
}

export default getRouter;
